import React, { useState, useEffect } from "react";
import axios from "axios";
import CONSTANTS from "../utils/constants";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const buildHierarchicalCategories = (
  categories,
  parentId = null,
  level = 0
) => {
  let hierarchicalList = [];
  const children = categories.filter((cat) => {
    if (parentId === null) {
      return !cat.parent_id;
    }
    return cat.parent_id === parentId;
  });
  children.sort((a, b) => (a.order_number || 0) - (b.order_number || 0));
  for (const cat of children) {
    hierarchicalList.push({ ...cat, level });
    const childItems = buildHierarchicalCategories(
      categories,
      cat.id,
      level + 1
    );
    hierarchicalList = hierarchicalList.concat(childItems);
  }
  return hierarchicalList;
};

export default function AdminCategoryAdd() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [categoryData, setCategoryData] = useState({
    name_tm: "",
    name_ru: "",
    name_en: "",
    parentCategory: "",
    orderNumber: 0,
    justCategory: false,
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [availableParentCategories, setAvailableParentCategories] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchAndFormatCategories = async () => {
    try {
      const response = await axios.get(`${CONSTANTS.baseRoute}/categories`);
      const allCats = response.data;
      setAllCategories(allCats);
      
      // Sadece just_category = false olan kategorileri göster
      const validParentCategories = allCats.filter(cat => !cat.just_category);
      
      // Hiyerarşik yapıyı oluştur
      const hierarchicalData = buildHierarchicalCategories(validParentCategories);
      setAvailableParentCategories(hierarchicalData);
    } catch (error) {
      console.error("Kategoriler çekilirken hata:", error);
      setMessage({ type: "danger", text: t("adminCategoryAdd.loadCategoriesError") });
    }
  };

  useEffect(() => {
    fetchAndFormatCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Eğer justCategory checkbox'ı değiştiyse
    if (name === "justCategory") {
      setCategoryData((prevState) => ({
        ...prevState,
        justCategory: checked,
        // justCategory true olduğunda parentCategory'yi temizle
        parentCategory: checked ? "" : prevState.parentCategory,
      }));
    } else {
      setCategoryData((prevState) => ({
        ...prevState,
        [name]: type === "checkbox" ? checked : (name === "orderNumber" ? Number(value) || 0 : value),
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setMessage({ type: "danger", text: t("adminCategoryAdd.onlyImagesAllowed") });
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const errors = [];

    if (!categoryData.name_tm.trim()) {
      errors.push(t("adminCategoryAdd.nameTmRequired"));
    }

    if (!imageFile) {
      errors.push(t("adminCategoryAdd.imageRequired"));
    }

    // Eğer justCategory false ise ve parent category seçilmişse, onun just_category kontrolü
    if (!categoryData.justCategory && categoryData.parentCategory) {
      const selectedParent = allCategories.find(cat => cat.id === parseInt(categoryData.parentCategory));
      if (selectedParent && selectedParent.just_category) {
        errors.push(t("adminCategoryAdd.cannotAddSubcategoryToJustCategory"));
      }
    }

    // justCategory true ise parentCategory boş olmalı
    if (categoryData.justCategory && categoryData.parentCategory) {
      errors.push(t("adminCategoryAdd.justCategoryCannotHaveParent"));
    }

    if (errors.length > 0) {
      setMessage({
        type: "danger",
        text: errors.join(" | "),
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setMessage({ type: "", text: "" });

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("name_tm", categoryData.name_tm.trim());
    formData.append("name_ru", categoryData.name_ru.trim() || "");
    formData.append("name_en", categoryData.name_en.trim() || "");
    
    if (categoryData.parentCategory && !categoryData.justCategory) {
      formData.append("parent_id", categoryData.parentCategory);
    }
    
    formData.append("order_number", categoryData.orderNumber);
    formData.append("just_category", categoryData.justCategory ? "1" : "0");

    if (imageFile) {
      formData.append("image", imageFile);
    }

    try {
      await axios.post(
        `${CONSTANTS.baseRoute}/categories`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setMessage({ 
        type: "success", 
        text: t("adminCategoryAdd.successMessage") 
      });
      
      setCategoryData({
        name_tm: "",
        name_ru: "",
        name_en: "",
        parentCategory: "",
        orderNumber: 0,
        justCategory: false,
      });
      setImageFile(null);
      setImagePreview(null);
      document.getElementById("imageUpload") && (document.getElementById("imageUpload").value = "");

      setTimeout(() => {
        navigate(-1);
      }, 1800);
    } catch (error) {
      const errorMessage = 
        error.response?.data?.message || 
        t("adminCategoryAdd.submitError");
      
      setMessage({ type: "danger", text: errorMessage });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Kategori adını seviyeye göre formatla
  const formatCategoryName = (category) => {
    const indent = "—".repeat(category.level);
    return indent ? `${indent} ${category.name_tm}` : category.name_tm;
  };

  return (
    <div className="admin-main ps-2 pe-3 pb-5">
      <div className="d-flex gap-2 align-items-center my-3">
        <button className="btn btn-light" onClick={() => navigate(-1)}>
          <i className="bi bi-chevron-left"></i>
        </button>
        <button className="btn btn-light" onClick={() => navigate(1)}>
          <i className="bi bi-chevron-right"></i>
        </button>
        <h5 className="fw-bold mb-0">{t("adminCategoryAdd.title")}</h5>
      </div>

      {message.text && (
        <div 
          className={`alert alert-${message.type} shadow-sm mb-4 sticky-top`} 
          style={{ top: "70px", zIndex: 1000 }}
          role="alert"
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-3" noValidate>
        <div className="row g-4">
          {/* Sol Kolon: Resim Yükleme */}
          <div className="col-lg-4 col-md-12">
            <div className="card h-100 shadow-sm border-0">
              <div className="card-body d-flex flex-column">
                <h5 className="card-title mb-3 fs-6 fw-bold">
                  {t("adminCategoryAdd.imageTitle")} <span className="text-danger">*</span>
                </h5>
                
                <input
                  type="file"
                  id="imageUpload"
                  className="d-none"
                  accept="image/png, image/jpeg, image/webp"
                  onChange={handleImageChange}
                />
                
                <label
                  htmlFor="imageUpload"
                  className={`image-uploader-box flex-grow-1 border rounded d-flex align-items-center justify-content-center overflow-hidden cursor-pointer ${
                    !imagePreview ? 'bg-light border-dashed' : ''
                  }`}
                  style={{ minHeight: "280px" }}
                >
                  {imagePreview ? (
                    <img 
                      src={imagePreview} 
                      alt={t("adminCategoryAdd.preview")} 
                      className="img-fluid h-100 w-100" 
                      style={{ objectFit: "contain" }} 
                    />
                  ) : (
                    <div className="text-center p-4">
                      <i className="bi bi-cloud-arrow-up fs-2 text-muted mb-3 d-block"></i>
                      <div className="text-muted mb-1">
                        {t("adminCategoryAdd.dragOrClick")}
                      </div>
                      <small className="text-secondary">
                        {t("adminCategoryAdd.supportedFormats")}
                      </small>
                    </div>
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* Sağ Kolon: Form Bilgileri */}
          <div className="col-lg-8 col-md-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title mb-4 fs-6 fw-bold">
                  {t("adminCategoryAdd.categoryInfo")}
                </h5>

                <div className="mb-4">
                  <label htmlFor="name_tm" className="form-label fw-semibold small">
                    {t("adminCategoryAdd.nameTm")} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="name_tm"
                    name="name_tm"
                    value={categoryData.name_tm}
                    onChange={handleChange}
                    placeholder={t("adminCategoryAdd.nameTmPlaceholder")}
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="name_ru" className="form-label fw-semibold small">
                    {t("adminCategoryAdd.nameRu")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="name_ru"
                    name="name_ru"
                    value={categoryData.name_ru}
                    onChange={handleChange}
                    placeholder={t("adminCategoryAdd.nameRuPlaceholder")}
                  />
                </div>

                <div className="row g-3">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="parentCategory" className="form-label fw-semibold small">
                      {t("adminCategoryAdd.parentCategory")}
                    </label>
                    <select
                      className="form-select"
                      id="parentCategory"
                      name="parentCategory"
                      value={categoryData.parentCategory}
                      onChange={handleChange}
                      disabled={categoryData.justCategory} // justCategory true iken select disabled
                    >
                      <option value="">
                        {t("adminCategoryAdd.noParent")}
                      </option>
                      {/* justCategory false iken diğer seçenekleri göster */}
                      {!categoryData.justCategory && availableParentCategories
                        .filter((cat) => cat.level === 0)
                        .map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name_tm}
                          </option>
                        ))}
                    </select>
                    <small className="text-muted d-block mt-1">
                      {categoryData.justCategory 
                        ? t("adminCategoryAdd.justCategoryNoParent") 
                        : t("adminCategoryAdd.onlyNonJustCategories")}
                    </small>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="orderNumber" className="form-label fw-semibold small">
                      {t("adminCategoryAdd.orderNumber")}
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="orderNumber"
                      name="orderNumber"
                      value={categoryData.orderNumber}
                      onChange={handleChange}
                      min="0"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Eğer parent category seçilmişse ve just_category ise uyarı göster */}
                {!categoryData.justCategory && categoryData.parentCategory && (
                  (() => {
                    const selectedParent = allCategories.find(cat => cat.id === parseInt(categoryData.parentCategory));
                    if (selectedParent && selectedParent.just_category) {
                      return (
                        <div className="alert alert-warning mt-3 py-2">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          {t("adminCategoryAdd.parentIsJustCategoryWarning")}
                        </div>
                      );
                    }
                    return null;
                  })()
                )}

                {/* justCategory true iken bilgi mesajı */}
                {categoryData.justCategory && (
                  <div className="alert alert-info mt-3 py-2">
                    <i className="bi bi-info-circle me-2"></i>
                    {t("adminCategoryAdd.justCategoryInfo")}
                  </div>
                )}

                <div className="d-flex justify-content-end mt-5">
                  <div className="d-flex justify-content-between align-items-center w-25">
                    <span className="">{t('justcategory')}:</span>
                    <label className="switch">
                      <input
                        type="checkbox"
                        name="justCategory" 
                        checked={categoryData.justCategory}
                        onChange={handleChange} 
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                  <button 
                    type="submit" 
                    className="btn btn-primary px-5 py-2 fw-bold"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {t("common.saving")}
                      </>
                    ) : (
                      t("common.save")
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}